/* ======================================================================
   PALLET PACKER — pure JavaScript, no dependencies
   Lifted from the Weblly visualiser. Computes where every box goes
   BEFORE anything is drawn. The renderer never decides placement.
   ====================================================================== */

/* Pallet build rules — owned by the app, not by the cart data */
const RULES = { W: 40, L: 48, maxH: 72, maxWeight: 2200, maxCases: 120, rotate: true, deck: 5.5 };

function chooseOrient(s, cfg){
  const a = { fw:s.w, fl:s.l, n:Math.floor(cfg.W/s.w)*Math.floor(cfg.L/s.l) };
  const b = { fw:s.l, fl:s.w, n:Math.floor(cfg.W/s.l)*Math.floor(cfg.L/s.w) };
  const best = (cfg.rotate && b.n > a.n) ? b : a;
  s.fw = best.fw; s.fl = best.fl; s.uprightH = s.h;
}

function packOnePallet(skus, cfg, warnings){
  const pal = { boxes:[], weight:0, cases:0, height:0, skuCount:{} };
  let cx=0, cz=0, rowDepth=0; const E=1e-6;
  function tryPlace(fw, fl){
    if(cx+fw <= cfg.W+E && cz+fl <= cfg.L+E){
      const s={x:cx,z:cz}; cx+=fw; rowDepth=Math.max(rowDepth,fl); return s;
    }
    const ncz = cz+rowDepth;
    if(fw <= cfg.W+E && ncz+fl <= cfg.L+E){
      cz=ncz; cx=0; rowDepth=0;
      const s={x:cx,z:cz}; cx+=fw; rowDepth=Math.max(rowDepth,fl); return s;
    }
    return null;
  }
  const active = skus.filter(s=>{
    if(s.remaining<=0) return false;
    if(s.fw>cfg.W+E || s.fl>cfg.L+E){
      warnings.push(`${s.name}: case footprint ${s.fw}×${s.fl}" exceeds the ${cfg.W}×${cfg.L}" pallet.`);
      s.remaining=0; return false;
    }
    return true;
  });
  if(!active.length){ centerPallet(pal,cfg); return pal; }

  const maxLayersFor = s => Math.max(1, Math.floor(cfg.maxH/s.h));
  const cols = {}; active.forEach(s=>cols[s.sku]=[]);

  let guard=0;
  while(guard++<4000){
    const ranked = active.filter(s=>s.remaining>0 && cols[s.sku].length<s.remaining)
      .sort((a,b)=>{
        const ra=a.remaining/(cols[a.sku].length+1), rb=b.remaining/(cols[b.sku].length+1);
        if(rb!==ra) return rb-ra;
        return (b.fw*b.fl)-(a.fw*a.fl);
      });
    if(!ranked.length) break;
    let placed=false;
    for(const s of ranked){
      const slot=tryPlace(s.fw,s.fl);
      if(slot){ cols[s.sku].push(slot); placed=true; break; }
    }
    if(!placed) break;
  }

  for(const s of active){
    const myCols = cols[s.sku];
    if(!myCols.length) continue;
    const cap = maxLayersFor(s);
    let allow = Math.min(s.remaining, myCols.length*cap);
    allow = Math.min(allow, cfg.maxCases - pal.cases);
    if(s.maxPerPallet!=null) allow = Math.min(allow, s.maxPerPallet-(pal.skuCount[s.sku]||0));
    allow = Math.min(allow, Math.floor((cfg.maxWeight-pal.weight)/s.weight));
    if(allow<=0) continue;
    const base=Math.floor(allow/myCols.length), extra=allow%myCols.length;
    myCols.forEach((slot,ci)=>{
      const layers = base + (ci<extra?1:0);
      for(let k=0;k<layers;k++){
        pal.boxes.push({x:slot.x,z:slot.z,y:k*s.h,w:s.fw,l:s.fl,h:s.h,
          sku:s.sku,name:s.name,color:s.color,stackable:s.stackable});
        pal.cases++; pal.weight+=s.weight;
        pal.skuCount[s.sku]=(pal.skuCount[s.sku]||0)+1;
        pal.height=Math.max(pal.height,(k+1)*s.h);
        s.remaining--;
      }
    });
  }
  centerPallet(pal,cfg);
  return pal;
}

function centerPallet(pal, cfg){
  if(!pal.boxes.length) return;
  let minX=Infinity,maxX=-Infinity,minZ=Infinity,maxZ=-Infinity;
  pal.boxes.forEach(b=>{
    minX=Math.min(minX,b.x); maxX=Math.max(maxX,b.x+b.w);
    minZ=Math.min(minZ,b.z); maxZ=Math.max(maxZ,b.z+b.l);
  });
  const shiftX=(cfg.W-(maxX-minX))/2-minX, shiftZ=(cfg.L-(maxZ-minZ))/2-minZ;
  pal.boxes.forEach(b=>{ b.x+=shiftX; b.z+=shiftZ; });
}

function packAuto(cart, cfg){
  const skus = cart.filter(it=>it.qty>0).map(it=>({
    sku:it.sku, name:it.name, color:it.color,
    l:it.case.l, w:it.case.w, h:it.case.h, weight:it.weight,
    stackable:true, maxPerPallet:it.maxPerPallet, remaining:it.qty
  }));
  skus.forEach(s=>chooseOrient(s,cfg));
  skus.sort((a,b)=>(b.fw*b.fl)-(a.fw*a.fl));
  const pallets=[], warnings=[];
  let guard=0;
  while(skus.some(s=>s.remaining>0) && guard++<600){
    const pal=packOnePallet(skus,cfg,warnings);
    if(pal.cases===0){
      const bad=skus.find(s=>s.remaining>0);
      if(bad){ warnings.push(`Could not place ${bad.name}.`); bad.remaining=0; }
      continue;
    }
    pallets.push(pal);
  }
  if(!pallets.length) pallets.push({boxes:[],weight:0,cases:0,height:0,skuCount:{}});
  return { pallets, warnings, cfg };
}
